import {
  Component,
  computed,
  inject,
  input,
  output,
  Signal,
  signal,
  effect,
  DestroyRef,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { LoadSegment } from '../../../core/models/load-segment.model';
import { CommonModule } from '@angular/common';
import { Load, LoadWithView } from '../../../core/models/load.model';
import { HeadingComponent } from '../../../shared/ui/typography/heading/heading.component';
import { TextComponent } from '../../../shared/ui/typography/text/text.component';
import { BadgeComponent } from '../../../shared/ui/badge/badge.component';
import { LucideIconComponent } from '../../../shared/ui/lucide-icon/lucide-icon.component';
import { CardComponent } from '../../../shared/ui/layout/card/card.component';
import { formatTemperature } from '../../../shared/utilities/formatters';
import { calculateRatePerMile } from '../../../shared/utilities/calculations';
import { LoadStopCardComponent } from './load-stop-card/load-stop-card.component';
import { ModalService } from '../../../core/services/modal.service';
import { EditAssignmentsFormComponent } from './edit-assignments-form/edit-assignments-form.component';
import { FleetService } from '../../../core/services/fleet.service';
import { EditDriversFormComponent } from './edit-drivers-form/edit-drivers-form.component';
import { ButtonComponent } from '../../../shared/ui/button/button.component';
import { Router } from '@angular/router';
import { EditDetailsFormComponent } from './edit-details-form/edit-details-form.component';
import { EnumLabelPipe } from '../../../shared/pipes/enum-label.pipe';
import { EditNotesFormComponent } from './edit-notes-form/edit-notes-form.component';
import { EditStopFormComponent } from './edit-stop-form/edit-stop-form.component';
import { EditRouteFormComponent } from './edit-route-form/edit-route-form.component';
import { DurationPipe } from '../../../shared/pipes/duration.pipe';
import { DocumentViewerService, DocumentType } from '../../../core/services/document-viewer.service';
import { LoadingService } from '../../../shared/ui/loading/loading.service';
import { LoadService } from '../../../core/services/load.service';
import { ToastService } from '../../../shared/ui/toast/toast.service';
import { LoadStatusType } from '../../../core/enums/load-status.enum';
import { ColorVariant } from '../../../core/enums/types';
import {
  LoadDetailsComponentState,
  LoadStateService,
} from '../../../core/services/load-state.service';
import { LoadValidationService, LoadValidationResult } from '../../../core/services/load-validation.service';
import { UserService } from '../../../core/services/user.service';
import { AssetService } from '../../../core/services/asset.service';
import { StatusUpdateModalComponent } from './status-update-modal/status-update-modal.component';
import { EmailDocumentsModalComponent } from './email-documents-modal/email-documents-modal.component';
import { SegmentService } from '../../../core/services/segment.service';
import { AddStopModalComponent } from './add-stop-modal/add-stop-modal.component';
import { LegAssignmentModalComponent } from './leg-assignment-modal/leg-assignment-modal.component';
import { LegPayModalComponent } from './leg-pay-modal/leg-pay-modal.component';
import { MergeLegModalComponent } from './merge-leg-modal/merge-leg-modal.component';
import { AdjustLegPayEvent, LoadPaySummaryComponent } from './load-pay-summary/load-pay-summary.component';
import { Stop } from '../../../core/models/stop.model';
import { PermissionsService } from '../../../core/services/permissions.service';
import { AuditLogTimelineComponent } from './audit-log-timeline/audit-log-timeline.component';
import { DropdownComponent, DropdownOption } from '../../../shared/ui/dropdown/dropdown.component';
import { CancelLoadModalComponent } from './cancel-load-modal/cancel-load-modal.component';
import { AuthService } from '../../../core/services/auth.service';
import { StickyNoteService } from '../../../core/services/sticky-note.service';
import { LoadStickyNote } from '../../../core/models/sticky-note.model';
import { LoadStickyNotesComponent } from './load-sticky-notes/load-sticky-notes.component';

@Component({
  selector: 'app-load-details',
  standalone: true,
  imports: [
    CommonModule,
    HeadingComponent,
    TextComponent,
    BadgeComponent,
    LucideIconComponent,
    CardComponent,
    LoadStopCardComponent,
    ButtonComponent,
    EnumLabelPipe,
    DurationPipe,
    AuditLogTimelineComponent,
    DropdownComponent,
    LoadStickyNotesComponent,
    LoadPaySummaryComponent,
  ],
  templateUrl: './load-details.component.html',
})
export class LoadDetailsComponent {
  private readonly router = inject(Router);
  private readonly modalService = inject(ModalService);
  private readonly fleetService = inject(FleetService);
  private readonly documentViewer = inject(DocumentViewerService);
  private readonly loadingService = inject(LoadingService);
  private readonly loadService = inject(LoadService);
  private readonly toastService = inject(ToastService);
  private readonly loadStateService = inject(LoadStateService);
  private readonly loadValidationService = inject(LoadValidationService);
  private readonly userService = inject(UserService);
  private readonly assetService = inject(AssetService);
  private readonly segmentService = inject(SegmentService);
  protected readonly perms = inject(PermissionsService);
  protected readonly canViewAuditLog = this.perms.canViewAuditLog;
  private readonly authService = inject(AuthService);
  private readonly stickyNoteService = inject(StickyNoteService);
  private readonly destroyRef = inject(DestroyRef);
  private segmentsSub?: Subscription;
  private stickyNotesSub?: Subscription;

  protected readonly stickyNotes = signal<LoadStickyNote[]>([]);
  protected readonly stickyBoardOpen = signal<boolean>(false);
  protected readonly stickyStopFilter = signal<number | null>(null);
  protected readonly activeCompanyId = computed(
    () => this.fleetService.currentCompanySignal()?.id || this.authService.companyId() || ''
  );

  constructor() {
    effect(() => {
      const load = this.load();
      this.segmentsSub?.unsubscribe();
      if (load?.id) {
        this.segmentsSub = this.segmentService.getSegments(load.id).subscribe(
          segs => this.segments.set(segs)
        );
      } else {
        this.segments.set([]);
      }
    });

    effect(() => {
      const loadId = this.load()?.id;
      const companyId = this.activeCompanyId();
      this.stickyNotesSub?.unsubscribe();
      this.stickyStopFilter.set(null);
      if (loadId && companyId) {
        this.stickyNotesSub = this.stickyNoteService
          .watchLoadNotes$(companyId, loadId)
          .subscribe((notes) => this.stickyNotes.set(notes));
      } else {
        this.stickyNotes.set([]);
      }
    });

    this.destroyRef.onDestroy(() => {
      this.segmentsSub?.unsubscribe();
      this.stickyNotesSub?.unsubscribe();
    });
  }

  toggleStickyBoard(): void {
    const next = !this.stickyBoardOpen();
    if (!next) {
      this.stickyStopFilter.set(null);
    }
    this.stickyBoardOpen.set(next);
  }

  openStickyNotesForStop(stopNumber?: number | null): void {
    const target = stopNumber ?? null;
    if (this.stickyBoardOpen() && this.stickyStopFilter() === target) {
      this.toggleStickyBoard();
      return;
    }
    this.stickyStopFilter.set(target);
    this.stickyBoardOpen.set(true);
  }

  getStopStickyCount(stopNumber?: number | null): number {
    if (stopNumber == null) return 0;
    return this.stickyNotes().filter((n) => n.stopNumber === stopNumber).length;
  }

  protected readonly fleetDrivers = this.fleetService.companyDriversSignal;
  protected readonly fleetTrucks = this.fleetService.companyTrucksSignal;
  protected readonly fleetTrailers = this.fleetService.companyTrailersSignal;

  // Input signals
  id = input.required<string>();
  load = input<LoadWithView | null>(null);
  showDetailsRoute = input<boolean>(true);

  // NEW: Context to determine where the component is being used
  viewContext = input<'details-page' | 'list-page'>('details-page');

  // Output event to notify parent when load is updated
  loadUpdated = output<void>();

  // Output event for back navigation
  backClicked = output<void>();

  protected readonly isLoading = signal(false);
  protected readonly segments = signal<LoadSegment[]>([]);

  // Computed: Full dispatch sheet URL
  protected readonly fullDispatchSheetUrl = computed<string | null>(() => {
    const load = this.load();
    if (!load) return null;
    if (load.documents?.fullDispatchSheet?.url) {
      return load.documents.fullDispatchSheet.url;
    }
    // Backward compatibility: if latestDispatchSheet exists without a segmentId, it's the full sheet
    const latest = load.documents?.latestDispatchSheet as any;
    if (latest?.url && !latest?.segmentId) {
      return latest.url;
    }
    // If only 1 segment exists and latest has a URL, treat it as full/only sheet
    if (this.segments().length <= 1 && latest?.url) {
      return latest.url;
    }
    return null;
  });

  protected readonly hasFullDispatchSheet = computed(() => !!this.fullDispatchSheetUrl());

  getSegmentDispatchSheetUrl(segment: LoadSegment): string | null {
    if (segment.dispatchSheetUrl) {
      return segment.dispatchSheetUrl;
    }
    const load = this.load();
    const segmentDoc = (load?.documents?.segmentDispatchSheets as any)?.[segment.id];
    if (segmentDoc?.url) {
      return segmentDoc.url;
    }
    // Check if latestDispatchSheet was explicitly tagged for this segment
    const latest = load?.documents?.latestDispatchSheet as any;
    if (latest?.segmentId === segment.id && latest?.url) {
      return latest.url;
    }
    // If only 1 segment exists in the entire load and latestDispatchSheet exists, that sheet is for this segment!
    if (this.segments().length <= 1 && latest?.url) {
      return latest.url;
    }
    return null;
  }

  hasSegmentDispatchSheet(segment: LoadSegment): boolean {
    return !!this.getSegmentDispatchSheetUrl(segment);
  }

  protected readonly hasAnyDispatchSheet = computed(() => {
    if (this.hasFullDispatchSheet()) return true;
    return this.segments().some(s => this.hasSegmentDispatchSheet(s));
  });

  protected readonly availableDocumentsCount = computed(() => {
    const load = this.load();
    if (!load) return 0;
    let count = 0;
    if (load.view?.documents?.rateConfirmation?.url) count++;
    if (load.view?.documents?.invoice?.url) count++;
    if (this.hasFullDispatchSheet()) count++;
    for (const segment of this.segments()) {
      if (this.hasSegmentDispatchSheet(segment)) count++;
    }
    return count;
  });

  // Computed: Check if dispatch sheet exists (used by smart load state)
  protected readonly hasDispatchSheet = computed(() => {
    const load = this.load();
    return !!load?.documents?.latestDispatchSheet || this.hasAnyDispatchSheet();
  });

  // Smart Load State
  protected readonly loadState = this.loadStateService.computeLoadState(
    this.load,
    this.hasDispatchSheet,
    {
      onMarkVerified: () => this.handleMarkVerified(),
      onAssignAssets: () => this.handleAssignAssets(),
      onGenerateDispatch: () => this.generateDispatchSheet(),
      onProvideETA: () => this.handleProvideETA(),
      onStatusUpdate: () => this.handleStatusUpdate(),
      onResolveActionRequired: () => this.handleResolveActionRequired(),
    }
  );

  // Legacy computed properties (keep for template compatibility)
  protected readonly externalLoadId = computed(() => {
    const load = this.load();
    return load ? load.externalId : null;
  });

  protected readonly bookedUnderName = computed(() => {
    const load = this.load();
    if (!load) return 'Unknown';
    if (load.carrierName && load.carrierName !== 'Your Company') {
      return load.carrierName;
    }
    return this.fleetService.currentCompanySignal()?.legalName || 'Unknown';
  });

  protected readonly bookedFromParty = computed(() => {
    const load = this.load();
    if (!load) return null;
    const view = load.view;
    const name = view?.bookedFromCompanyName || view?.brokerName || view?.customerName;
    if (!name) return null;
    const role = view?.bookedFromRole || (view?.customerName ? 'direct_shipper' : 'broker');
    const dotNumber = view?.bookedFromDotNumber || view?.brokerDotNumber || view?.customerDotNumber;
    return { name, role, dotNumber };
  });

  protected readonly rateConfirmationOptions = computed<DropdownOption[]>(() => {
    const versions = this.load()?.view?.documents?.rateConfirmations;
    if (!versions || versions.length <= 1) return [];
    return versions
      .filter((v): v is typeof v & { url: string } => typeof v.url === 'string' && v.url.length > 0)
      .map((v, i) => {
        const vNum = v.version || (versions.length - i);
        const isLatest = i === 0;
        return {
          value: v.url,
          label: isLatest ? `Rate Con v${vNum} (Latest)` : `Rate Con v${vNum}`,
        };
      });
  });

  protected readonly carrierOfRecordName = computed(() => {
    const load = this.load();
    if (!load) return 'Unknown';
    if (load.carrierOfRecordCompanyName) return load.carrierOfRecordCompanyName;
    if (load.carrierName && load.carrierName !== 'Your Company') {
      return load.carrierName;
    }
    return this.fleetService.currentCompanySignal()?.legalName || 'Your Fleet';
  });

  protected readonly isCarrierForwarded = computed(() => {
    const l = this.load();
    if (!l) return false;
    const view = l.view;

    // Carrier of Record info on this load
    const carrierId = l.carrierOfRecordCompanyId || view?.carrierOfRecordCompanyId || l.carrierId;
    const carrierName = this.carrierOfRecordName();
    const carrierDot = l.carrierOfRecordDotNumber || l.carrierDotNumber || view?.bookedFromDotNumber;

    // Current logged-in user's company info
    const currentComp = this.fleetService.currentCompanySignal();
    const currentCompId = currentComp?.id || this.authService.companyId();
    const currentLegalName = currentComp?.legalName?.trim().toLowerCase();
    const currentDbaName = currentComp?.dbaName?.trim().toLowerCase();
    const currentDot = currentComp?.dotNumber;

    // ── CRITICAL: If the carrier of record is the logged in user's carrier company, NEVER SHOW ──
    if (carrierId && currentCompId && carrierId === currentCompId) {
      return false;
    }
    if (carrierName) {
      const normalizedCarrierName = carrierName.trim().toLowerCase();
      if (normalizedCarrierName === 'your company' || normalizedCarrierName === 'your fleet' || normalizedCarrierName === 'unknown') {
        return false;
      }
      if (currentLegalName && currentLegalName !== 'your company' && normalizedCarrierName === currentLegalName) {
        return false;
      }
      if (currentDbaName && normalizedCarrierName === currentDbaName) {
        return false;
      }
    }
    if (carrierDot && currentDot && carrierDot === currentDot) {
      return false;
    }

    // Now determine if this load was genuinely forwarded from a 3rd-party carrier:
    // 1. Explicitly booked from another carrier
    if (view?.bookedFromRole === 'carrier' && view?.bookedFromCompanyId !== currentCompId) {
      return true;
    }

    // 2. Carrier of record is an external company ID
    if (carrierId && currentCompId && carrierId !== currentCompId) {
      return true;
    }

    // 3. Carrier name is present and clearly distinct from own company
    if (carrierName && currentLegalName && currentLegalName !== 'your company' && carrierName.trim().toLowerCase() !== currentLegalName) {
      return true;
    }

    // 4. Has original broker set and a distinct carrier of record
    const origBroker = l.originalBrokerCompanyName;
    if (origBroker && carrierName && origBroker.trim().toLowerCase() !== carrierName.trim().toLowerCase()) {
      return true;
    }

    return false;
  });

  protected readonly carrierOfRecordTooltip = computed(() => {
    const l = this.load();
    if (!l) return '';
    const carrierName = this.carrierOfRecordName();
    const carrierDot = l.carrierOfRecordDotNumber || l.carrierDotNumber;
    const origBroker = l.originalBrokerCompanyName || l.view?.brokerName;
    const parts: string[] = [];
    if (carrierName) {
      parts.push(`Carrier of Record: ${carrierName}${carrierDot ? ` (USDOT ${carrierDot})` : ''}`);
    }
    if (origBroker) {
      parts.push(`Original Broker: ${origBroker}`);
    }
    parts.push('Load forwarded by another carrier');
    return parts.join(' • ');
  });

  protected readonly operatedByParty = computed(() => {
    const load = this.load();
    if (!load) return null;
    const carrier = this.carrierOfRecordName();
    const opName = load.operatedByCompanyName || load.hauledByName;
    if (!opName || opName === carrier || opName === 'Your Company') {
      return null;
    }
    const role = load.operatedByRole || (load.forwardedToOwnerOp ? 'owner_operator' : 'carrier');
    return { name: opName, role };
  });

  protected readonly hauledByName = computed(() => {
    const load = this.load();
    if (!load) return 'Unassigned';
    if (load.hauledByName && load.hauledByName !== 'Your Company') {
      return load.hauledByName;
    }
    if (load.hauledById) {
      // If hauledById equals the current company, show the company name
      const currentCompany = this.fleetService.currentCompanySignal();
      if (currentCompany && load.hauledById === currentCompany.id) {
        return currentCompany.legalName;
      }
      return load.hauledByName || 'Unassigned';
    }
    return 'Unassigned';
  });

  // Update these computed properties to use Preferred Name when set (Legal Name remains on Dispatch Sheets)
  protected readonly driver1 = computed(() => {
    const load = this.load();
    if (!load || !load.driver1Id) return 'Unassigned';
    return this.userService.getDriverDisplayName(load.driver1Id, load.driver1Name || 'Unassigned');
  });

  protected readonly driver2 = computed(() => {
    const load = this.load();
    if (!load || !load.driver2Id) return 'Unassigned';
    return this.userService.getDriverDisplayName(load.driver2Id, load.driver2Name || 'Unassigned');
  });

  protected readonly isMultiLeg = computed(() => this.segments().length > 1);

  protected isSegmentSettled(segment: LoadSegment): boolean {
    return !!(segment.settlementIds && Object.keys(segment.settlementIds).length > 0);
  }

  protected getSegmentDriverDisplay(segment: LoadSegment): string {
    const primary = segment.driverId
      ? this.userService.getDriverDisplayName(segment.driverId, segment.driverName || 'Driver')
      : segment.driverName || 'Needs Driver';
    if (segment.driver2Id) {
      const coDriver = this.userService.getDriverDisplayName(
        segment.driver2Id,
        segment.driver2Name || 'Co-Driver'
      );
      return `${primary} & ${coDriver}`;
    }
    return primary;
  }

  protected getSegmentMilesDisplay(segment: LoadSegment): number {
    if (segment.segmentMiles != null && segment.segmentMiles > 0) {
      return segment.segmentMiles;
    }
    const stops = this.load()?.stops || [];
    let miles = 0;
    for (const s of stops) {
      if (
        s.stopNumber != null &&
        s.stopNumber >= segment.fromStopNumber &&
        s.stopNumber < segment.toStopNumber
      ) {
        miles += Number(s.milesToNext) || 0;
      }
    }
    return Math.round(miles);
  }

  protected canSplitAtStop(stop: Stop): boolean {
    const load = this.load();
    if (!load || !this.loadState().canAddHandoff) return false;
    const totalStops = load.stops?.length || 0;
    const sn = stop.stopNumber ?? 0;
    if (sn <= 1 || sn >= totalStops) return false;
    if (stop.handoffToSegmentId) return false;
    if (stop.status === 'DEPARTED') return false;
    return true;
  }

  protected getStopServicedByLabel(stop: Stop): string | null {
    if (!this.isMultiLeg()) return null;
    const isCommercial =
      stop.stopType === 'pickup' || stop.stopType === 'delivery' || stop.stopType === 'both';
    if (!isCommercial) return null;
    if (!stop.handoffToSegmentId && !stop.servicedBySegmentId) return null;

    const segs = this.segments();
    const servicing =
      (stop.servicedBySegmentId
        ? segs.find((s) => s.id === stop.servicedBySegmentId)
        : null) ||
      segs.find((s) => s.toStopNumber === stop.stopNumber) ||
      null;
    if (!servicing) return null;
    return `Dock: Leg ${servicing.segmentNumber} (${this.getSegmentDriverDisplay(servicing)})`;
  }

  protected readonly legGroups = computed(() => {
    const load = this.load();
    const segs = this.segments();
    if (!load || segs.length <= 1) return [];

    const stops = load.stops || [];
    return segs.map((segment, index) => {
      const previousSegment = index > 0 ? segs[index - 1] : null;
      const boundaryEntryStop =
        index > 0
          ? stops.find((s) => s.stopNumber === segment.fromStopNumber) || null
          : null;
      const exclusiveStops =
        index === 0
          ? stops.filter(
              (s) =>
                s.stopNumber != null &&
                s.stopNumber >= segment.fromStopNumber &&
                s.stopNumber <= segment.toStopNumber
            )
          : stops.filter(
              (s) =>
                s.stopNumber != null &&
                s.stopNumber > segment.fromStopNumber &&
                s.stopNumber <= segment.toStopNumber
            );

      const canUndoSplit =
        index > 0 &&
        !this.isSegmentSettled(segment) &&
        this.loadState().canAddHandoff &&
        this.getSegmentEffectiveStatus(segment) !== 'completed';

      return {
        segment,
        previousSegment,
        boundaryEntryStop,
        exclusiveStops,
        isFirst: index === 0,
        isLast: index === segs.length - 1,
        driverDisplay: this.getSegmentDriverDisplay(segment),
        miles: this.getSegmentMilesDisplay(segment),
        canUndoSplit,
      };
    });
  });

  protected readonly truck = computed(() => {
    const load = this.load();
    if (!load || !load.truckId) return 'Unassigned';

    // Get truck from AssetService
    const truck = this.assetService.getAssetById(load.truckId);
    return truck?.unitNumber || truck?.licensePlate || 'Unassigned';
  });

  protected readonly trailer = computed(() => {
    const load = this.load();
    if (!load || !load.trailerId) return 'Unassigned';

    // Get trailer from AssetService
    const trailer = this.assetService.getAssetById(load.trailerId);
    return trailer?.unitNumber || trailer?.licensePlate || 'Unassigned';
  });

  protected readonly pickCount = computed(() => {
    return this.load()?.stops.filter((s) => s.stopType === 'pickup').length;
  });

  protected readonly deliveryCount = computed(() => {
    return this.load()?.stops.filter((s) => s.stopType === 'delivery').length;
  });

  protected readonly temperatureDisplay = computed(() => {
    const trailerType = this.load()?.trailerType;
    if (trailerType !== 'REEFER' && trailerType !== 'REEFER_TEAM') return null;
    const temp = this.load()?.temperature;
    if (!temp) return null;
    return formatTemperature(temp);
  });

  protected readonly billingBadgeVariant = computed(() => {
    // billingStatus lives in the company view
    const status = (this.load() as any)?.view?.billingStatus;
    switch (status) {
      case 'READY_TO_INVOICE': return 'primary';
      case 'INVOICED':         return 'info';
      case 'PAYMENT_PENDING':
      case 'PARTIALLY_PAID':  return 'warning';
      case 'PAID':            return 'success';
      case 'OVERDUE':
      case 'DISPUTED':        return 'danger';
      default:                return 'secondary';
    }
  });

  protected readonly draftValidation = computed<LoadValidationResult>(() => {
    const load = this.load();
    if (!load || load.status !== 'DRAFT') {
      return { isValid: true, errors: [] };
    }
    return this.loadValidationService.validateDraftLoad(load);
  });

  fixValidationError(field: string): void {
    const load = this.load();
    if (!load) return;
    if (field === 'carrierOfRecord' || field === 'bookedFrom') {
      this.openEditModal('assignments', load);
    } else if (field === 'temperature') {
      this.openEditModal('details', load);
    } else if (field === 'stops' || field === 'stopDates') {
      this.openEditModal('route', load);
    }
  }

  // Status badge helpers for the aside header
  protected readonly statusBadgeVariant = computed((): ColorVariant => {
    const statusMap: Record<string, ColorVariant> = {
      DRAFT: 'warning', VERIFIED: 'primary', DISPATCHED: 'info',
      ACTIVE: 'info', IN_TRANSIT: 'info', AT_STOP: 'info',
      DELIVERED: 'success', CANCELLED: 'danger', ACTION_REQUIRED: 'warning',
    };
    return statusMap[this.load()?.status || ''] || 'secondary';
  });

  protected readonly statusBadgeIcon = computed(() => {
    const iconMap: Record<string, string> = {
      DRAFT: 'FilePen', VERIFIED: 'ShieldCheck', DISPATCHED: 'Send',
      ACTIVE: 'Truck', IN_TRANSIT: 'Route', AT_STOP: 'MapPin',
      DELIVERED: 'CircleCheckBig', CANCELLED: 'XCircle', ACTION_REQUIRED: 'AlertTriangle',
    };
    return iconMap[this.load()?.status || ''] || 'Circle';
  });

  protected readonly bookedRatePerMile = computed(() => {
    // bookedAtRate and forwardRate live in the company view
    const rate = (this.load() as any)?.view?.bookedAtRate;
    const miles = this.load()?.loadedMiles;
    return calculateRatePerMile(miles, rate);
  });

  protected readonly forwardedRatePerMile = computed(() => {
    const rate = (this.load() as any)?.view?.forwardRate;
    const miles = this.load()?.loadedMiles;
    return calculateRatePerMile(miles, rate);
  });

  // **NEW: Collapse State Signals**
  bookingCollapsed = signal(false);
  documentsCollapsed = signal(false);
  driverCollapsed = signal(false);
  detailsCollapsed = signal(false);
  notesCollapsed = signal(true); // Default collapsed as requested
  auditCollapsed = signal(true); // Default collapsed so timeline stays compact until needed

  // Computed state to check if everything is collapsed
  allCollapsed = computed(() => {
    return (
      this.bookingCollapsed() &&
      this.documentsCollapsed() &&
      this.driverCollapsed() &&
      this.detailsCollapsed() &&
      this.notesCollapsed() &&
      (!this.canViewAuditLog() || this.auditCollapsed())
    );
  });

  toggleAllSections(): void {
    const nextState = !this.allCollapsed();
    this.bookingCollapsed.set(nextState);
    this.documentsCollapsed.set(nextState);
    this.driverCollapsed.set(nextState);
    this.detailsCollapsed.set(nextState);
    this.notesCollapsed.set(nextState);
    if (this.canViewAuditLog()) {
      this.auditCollapsed.set(nextState);
    }
  }

  // **Dropdown Options for Dispatch Sheets**
  protected readonly dispatchSheetOptions = computed<DropdownOption[]>(() => {
    const segments = this.segments();
    const options: DropdownOption[] = [
      { value: 'full', label: 'Full Load Dispatch Sheet' }
    ];
    
    if (segments.length > 0) {
      for (const segment of segments) {
        const driverLabel = this.getSegmentDriverDisplay(segment);
        options.push({
          value: segment.id,
          label: `Leg ${segment.segmentNumber} Sheet: ${driverLabel} (Stops ${segment.fromStopNumber}–${segment.toStopNumber})`
        });
      }
    }
    
    return options;
  });

  handleDispatchSheetSelection(option: DropdownOption): void {
    if (option.value === 'full') {
      this.generateDispatchSheet();
    } else {
      this.generateDispatchSheet(option.value);
    }
  }

  // **NEW: Main Action Handler - The smart button! 🎯**
  executeMainAction(): void {
    const state = this.loadState();
    state.mainActionHandler();
  }

  // **State-specific handlers**
  private handleMarkVerified(): void {
    const load = this.load();
    if (!load) return;

    // Validate draft load requirements before verifying
    const validation = this.loadValidationService.validateDraftLoad(load);
    if (!validation.isValid) {
      this.toastService.show(
        'warning',
        validation.firstErrorMessage || 'Please complete all required load details before verifying.',
        {
          title: 'Validation Failed',
          duration: 5000,
        }
      );

      const firstField = validation.errors[0]?.field;
      if (firstField === 'carrierOfRecord' || firstField === 'bookedFrom') {
        this.openEditModal('assignments', load);
      } else if (firstField === 'temperature') {
        this.openEditModal('details', load);
      } else if (firstField === 'stops' || firstField === 'stopDates') {
        this.openEditModal('route', load);
      }
      return;
    }

    const hasAssets = !!(
      (load.driver1Id || load.driver2Id) &&
      load.truckId &&
      load.trailerId
    );

    const newStatus: LoadStatusType = hasAssets ? 'DISPATCHED' : 'VERIFIED';

    this.loadService.updateLoadStatus(load.id, newStatus, 'DRAFT', load.stops).subscribe({
      next: () => {
        this.toastService.show(
          'success',
          `Load marked as ${newStatus.toLowerCase()}`,
          {
            title: 'Status Updated',
            duration: 3000,
          }
        );
        this.loadUpdated.emit();
      },
      error: (_error) => {
        this.toastService.show('error', 'Failed to update load status', {
          title: 'Error',
          duration: 5000,
        });
      },
    });
  }

  private handleAssignAssets(): void {
    const load = this.load();
    if (!load) return;

    const externalLoadId = this.externalLoadId();

    this.modalService.open(EditDriversFormComponent, {
      data: { load, segments: this.segments() },
      title: `Assign Driver & Equipment - #${externalLoadId}`,
      subtitle: 'Assign drivers, truck, and trailer to this load',
      icon: 'Users',
      size: 'lg',
      onSuccess: () => {
        this.loadUpdated.emit();
      },
    });
  }

  /**
   * Opens the unified Split Route / Relay modal.
   */
  protected openSplitRouteModal(defaultStopNumber?: number): void {
    const load = this.load();
    if (!load) return;

    this.modalService.open(LegAssignmentModalComponent, {
      data: {
        mode: 'split',
        load,
        segments: this.segments(),
        defaultStopNumber,
      },
      title: `Split Route / Relay — #${this.externalLoadId()}`,
      subtitle: 'Hand off trailer or swap driver & equipment mid-route',
      icon: 'Split',
      size: 'xl',
      onSuccess: () => {
        this.loadUpdated.emit();
      },
    });
  }

  /**
   * Opens the Edit Leg modal for an existing segment (operational driver, equipment & relay instructions).
   */
  protected openEditLegModal(segment: LoadSegment): void {
    const load = this.load();
    if (!load) return;

    this.modalService.open(LegAssignmentModalComponent, {
      data: {
        mode: 'edit',
        load,
        segments: this.segments(),
        segment,
      },
      title: `Edit Leg ${segment.segmentNumber} — #${this.externalLoadId()}`,
      subtitle: `Stops ${segment.fromStopNumber}–${segment.toStopNumber} driver, equipment, and relay instructions`,
      icon: 'Pencil',
      size: 'xl',
      onSuccess: () => {
        this.loadUpdated.emit();
      },
    });
  }

  /**
   * Opens the dedicated Adjust Leg Pay modal (flat-rate base pay override or unpaid deadhead miles).
   */
  protected openAdjustLegPayModal(event: AdjustLegPayEvent): void {
    const load = this.load();
    if (!load) return;

    const seg = event.segment;
    const legTitle = seg ? `Leg ${seg.segmentNumber}` : 'Leg 1';
    this.modalService.open(LegPayModalComponent, {
      data: {
        load,
        segments: this.segments(),
        segment: seg,
        driverId: event.driverId,
      },
      title: `Adjust ${legTitle} Pay — #${this.externalLoadId()}`,
      subtitle: 'Configure custom flat-rate base pay or unpaid positioning / deadhead miles',
      icon: 'DollarSign',
      size: 'lg',
      onSuccess: () => {
        this.loadUpdated.emit();
      },
    });
  }

  /**
   * Opens the Undo Split / Merge Leg confirmation modal.
   */
  protected openMergeLegModal(segment: LoadSegment): void {
    const load = this.load();
    if (!load) return;
    const allSegments = this.segments();
    const idx = allSegments.findIndex((s) => s.id === segment.id);
    if (idx <= 0) return;

    const previousSegment = allSegments[idx - 1];
    this.modalService.open(MergeLegModalComponent, {
      data: {
        load,
        segment,
        previousSegment,
        allSegments,
      },
      title: `Undo Split — Merge Leg ${segment.segmentNumber} into Leg ${previousSegment.segmentNumber}`,
      subtitle: 'Reconcile stops, leg assignments, and driver views atomically',
      icon: 'Merge',
      size: 'md',
      onSuccess: () => {
        this.loadUpdated.emit();
      },
    });
  }

  protected getSegmentEffectiveStatus(segment: LoadSegment): 'pending' | 'active' | 'completed' {
    if (segment.status === 'completed') return 'completed';

    const stops = this.load()?.stops || [];
    const totalSegs = this.segments().length;
    const segmentStops = stops.filter(
      (s) => s.stopNumber != null && s.stopNumber >= segment.fromStopNumber && s.stopNumber <= segment.toStopNumber
    );

    if (segmentStops.length > 0) {
      const allDoneForLeg = segmentStops.every((s) => {
        if (s.status === 'DEPARTED') return true;
        // Shared relay exit stop: leg is completed once outgoing drop is done
        if (s.stopNumber === segment.toStopNumber && segment.segmentNumber < totalSegs) {
          return s.relay?.dropStatus === 'COMPLETED';
        }
        return false;
      });
      if (allDoneForLeg) return 'completed';

      const anyStarted = segmentStops.some(
        (s) => s.status && s.status !== 'PENDING'
      );
      if (anyStarted) return 'active';
    }

    return segment.status === 'active' ? 'active' : 'pending';
  }

  protected getSegmentBadgeVariant(segment: LoadSegment): ColorVariant {
    if (segment.needsDispatchAttention || !segment.driverId) return 'warning';
    const status = this.getSegmentEffectiveStatus(segment);
    if (status === 'completed') return 'success';
    if (status === 'active') return 'info';
    return 'secondary';
  }

  protected getSegmentStatusLabel(segment: LoadSegment): string {
    if (segment.needsDispatchAttention || !segment.driverId) return 'Needs Driver';
    if (this.isSegmentSettled(segment)) return 'Settled';
    const status = this.getSegmentEffectiveStatus(segment);
    if (status === 'completed') return 'Leg Completed';
    if (status === 'active') return 'Active';
    return 'Scheduled';
  }

  private handleProvideETA(): void {
    const load = this.load();
    if (!load) return;

    const externalLoadId = this.externalLoadId();

    this.modalService.open(StatusUpdateModalComponent, {
      data: { load, initialMode: 'provide_eta' },
      title: `Provide ETA to Pickup - #${externalLoadId}`,
      subtitle: 'Set estimated arrival time for Stop 1 (Pickup)',
      icon: 'Clock',
      size: 'xl',
      onSuccess: () => {
        this.loadUpdated.emit();
      },
    });
  }

  private handleStatusUpdate(): void {
    const load = this.load();
    if (!load) return;

    const externalLoadId = this.externalLoadId();

    this.modalService.open(StatusUpdateModalComponent, {
      data: { load },
      title: `Update Load Status - #${externalLoadId}`,
      subtitle: 'Track progress through each stop',
      icon: 'TrendingUp',
      size: 'xl',
      onSuccess: () => {
        this.loadUpdated.emit();
      },
    });
  }

  protected readonly dismissedResolvedBanner = signal(false);

  protected handleResolveActionRequired(): void {
    void this.openActionRequiredModal('resolve');
  }

  // Keep existing methods
  openFullScreen() {
    const loadId = this.id();
    if (loadId) {
      this.router.navigate(['/loads', loadId], {
        state: { load: this.load() },
      });
    }
  }

  openEditModal(section: string, load: Load): void {
    // Check if editing is allowed

    const canEdit = this.canEditSection(section, this.loadState);

    if (!canEdit) {
      this.toastService.show(
        'warning',
        `Cannot edit ${section} at this stage`,
        {
          title: 'Edit Restricted',
          duration: 3000,
        }
      );
      return;
    }

    const externalLoadId = this.externalLoadId();
    const componentMap: Record<string, any> = {
      assignments: {
        component: EditAssignmentsFormComponent,
        title: `Edit booking details - #${externalLoadId}`,
        icon: 'UserCheck',
      },
      drivers: {
        component: EditDriversFormComponent,
        title: `Edit Driver & Equipment - #${externalLoadId}`,
        icon: 'Truck',
      },
      details: {
        component: EditDetailsFormComponent,
        title: `Edit Load Details - #${externalLoadId}`,
        icon: 'Edit',
      },
      notes: {
        component: EditNotesFormComponent,
        title: `Edit Instruction & Notes - #${externalLoadId}`,
        icon: 'FileText',
      },
      route: {
        component: EditRouteFormComponent,
        title: `Edit Route - #${externalLoadId}`,
        icon: 'MapPin',
      },
    };

    const config = componentMap[section];



    if (config) {
      this.modalService.open(config.component, {
        data: { load, segments: this.segments() },
        title: config.title,
        subtitle: 'Make changes and save when ready',
        icon: config.icon,
        size: 'lg',
        onSuccess: () => {
          this.loadUpdated.emit();
        },
      });
    }
  }

  private canEditSection(
    section: string,
    state: Signal<LoadDetailsComponentState>
  ): boolean {
    const editPermissions: Record<string, keyof LoadDetailsComponentState> = {
      assignments: 'canEditBookingDetails',
      drivers: 'canEditAssetAssignments',
      details: 'canEditDetails',
      notes: 'canEditNotes',
      route: 'canEditRoute',
    };

    const permission = editPermissions[section];
    if (!permission) return false;

    // Call the signal to get the actual state value
    const stateValue = state();
    return stateValue[permission] as boolean;
  }

  openEditStopModal(load: Load, stopNumber?: number | null): void {
    const state = this.loadState();

    if (!state.canEditStops) {
      this.toastService.show('warning', 'Cannot edit stops at this stage', {
        title: 'Edit Restricted',
        duration: 3000,
      });
      return;
    }

    if (!stopNumber) return;

    const externalLoadId = this.externalLoadId();
    this.modalService.open(EditStopFormComponent, {
      data: { load, stopNumber },
      title: `Edit Stop ${stopNumber} - #${externalLoadId}`,
      subtitle: 'Update stop details and requirements',
      icon: 'MapPin',
      size: 'lg',
      onSuccess: () => {
        this.loadUpdated.emit();
      },
    });
  }

  openAddStopModal(insertAfterStopNumber: number): void {
    const load = this.load();
    if (!load || !this.loadState().canEditStops) return;

    this.modalService.open(AddStopModalComponent, {
      data: { load, insertAfterStopNumber, segments: this.segments() },
      title: 'Insert Stop',
      subtitle: `Add a stop after Stop ${insertAfterStopNumber} — optionally switch driver or equipment`,
      icon: 'MapPinPlus',
      size: 'xl',
      onSuccess: () => {
        this.loadUpdated.emit();
      }
    });
  }

  async generateDispatchSheet(segmentId?: string): Promise<void> {
    const load = this.load();
    const state = this.loadState();

    if (!load || !state.canGenerateDispatchSheet) {
      this.toastService.show(
        'warning',
        'Cannot generate dispatch sheet at this stage',
        {
          title: 'Action Restricted',
          duration: 3000,
        }
      );
      return;
    }

    this.isLoading.set(true);

    const segment = segmentId ? this.segments().find(s => s.id === segmentId) : null;
    const sheetTitle = segment 
      ? `Driver Sheet: ${segment.driverName || 'Segment ' + segment.segmentNumber}`
      : 'Full Load Dispatch Sheet';

    const alreadyExists = segment 
      ? this.hasSegmentDispatchSheet(segment)
      : this.hasFullDispatchSheet();

    this.loadingService.show({
      variant: 'overlay',
      title: alreadyExists
        ? `Regenerating ${sheetTitle}`
        : `Generating ${sheetTitle}`,
      subtitle: 'Please wait while we create your document...',
    });

    try {
      const pdfUrl = await this.loadService.createDispatchSheet(
        load.id,
        load.forwardedToOwnerOp,
        segmentId
      );

      // Immediately stamp the segment with the returned URL (both in Firestore and in local state)
      if (segmentId && pdfUrl) {
        try {
          await this.segmentService.updateSegmentDispatchSheet(
            load.id,
            segmentId,
            pdfUrl
          );
        } catch (err) {
          console.warn('[LoadDetails] Could not write segment doc directly:', err);
        }
        this.segments.update((segs) =>
          segs.map((s) =>
            s.id === segmentId ? { ...s, dispatchSheetUrl: pdfUrl } : s
          )
        );
      } else if (!segmentId && pdfUrl && this.segments().length === 1) {
        // If generating full sheet and there's only 1 segment, stamp it as well
        const firstSeg = this.segments()[0];
        if (firstSeg) {
          try {
            await this.segmentService.updateSegmentDispatchSheet(
              load.id,
              firstSeg.id,
              pdfUrl
            );
          } catch (err) {
            console.warn('[LoadDetails] Could not write segment doc directly:', err);
          }
          this.segments.update((segs) =>
            segs.map((s) =>
              s.id === firstSeg.id ? { ...s, dispatchSheetUrl: pdfUrl } : s
            )
          );
        }
      }

      this.loadingService.hide();

      this.toastService.show(
        'success',
        alreadyExists
          ? `${sheetTitle} regenerated successfully`
          : `${sheetTitle} generated successfully`,
        {
          title: 'Success',
          duration: 4000,
        }
      );

      this.loadUpdated.emit();
    } catch (error: any) {
      this.loadingService.hide();

      let errorMessage = 'Failed to generate dispatch sheet. Please try again.';

      if (error?.code === 'functions/unauthenticated') {
        errorMessage =
          'You are not authenticated. Please log in and try again.';
      } else if (error?.code === 'functions/invalid-argument') {
        errorMessage =
          'Invalid load data. Please refresh the page and try again.';
      } else if (error?.message) {
        errorMessage = error.message;
      }

      this.toastService.show('error', errorMessage, {
        title: 'Generation Failed',
        showClose: true,
        duration: 0,
      });
    } finally {
      this.isLoading.set(false);
    }
  }

  /** Opens the modal to email all documents on this load to a recipient. */
  openEmailDocumentsModal(): void {
    const load = this.load();
    if (!load) return;

    this.modalService.open(EmailDocumentsModalComponent, {
      data: { load },
      title: `Email Documents - #${this.externalLoadId()}`,
      subtitle: 'Send all documents on this load to an email address',
      icon: 'Mail',
      size: 'md',
    });
  }

  cancelLoad() {
    const load = this.load();
    const state = this.loadState();

    if (!load || !state.canCancelLoad) {
      this.toastService.show('warning', 'Cannot cancel load at this stage', {
        title: 'Action Restricted',
        duration: 3000,
      });
      return;
    }

    this.modalService.open(CancelLoadModalComponent, {
      data: {
        load,
        onDeleted: () => {
          this.router.navigate(['/loads']);
        },
        onCancelled: () => {
          this.loadUpdated.emit();
        },
      },
      title: `Cancel Load #${load.externalId || load.id}`,
      subtitle: 'Select cancellation reason and review billing & invoicing impact',
      icon: 'AlertTriangle',
      size: 'md',
    });
  }

  retryExtraction(load: LoadWithView): void {
    if (!load.processingFileUrl) return;
    this.toastService.show('info', 'Retrying AI extraction...', { title: 'Retrying', duration: 3000 });
    this.loadService
      .retryLoadExtraction(load.id, load.processingFileUrl, load.processingFileName || undefined)
      .subscribe({
        next: () => {
          this.toastService.show('success', 'Extraction completed successfully!', { duration: 4000 });
          this.loadUpdated.emit();
        },
        error: (err) => {
          this.toastService.show('error', `Retry failed: ${err?.message || 'Unknown error'}`);
        },
      });
  }

  dismissProcessingError(load: LoadWithView): void {
    this.loadService
      .updateLoad(load.id, { processingStatus: 'completed', processingError: null })
      .subscribe({
        next: () => {
          this.loadUpdated.emit();
          this.toastService.show('info', 'Switched to manual entry.', { duration: 3000 });
        },
      });
  }

  async reviveLoad() {
    const load = this.load();
    const state = this.loadState();

    if (!load || !state.canReviveLoad) {
      return;
    }

    this.isLoading.set(true);

    try {
      const companyId = this.authService.companyId() || undefined;
      await this.loadService.reviveLoad(load.id, load.lastStatus || 'DRAFT', companyId);
      this.toastService.show('success', 'Load revived successfully', {
        title: 'Revived',
        duration: 4000,
      });
      this.loadUpdated.emit();
    } catch {
      this.toastService.show('error', 'Failed to revive load', {
        title: 'Revival Failed',
        showClose: true,
        duration: 0,
      });
    } finally {
      this.isLoading.set(false);
    }
  }

  async openActionRequiredModal(mode: 'flag' | 'resolve', isEditingExisting = false): Promise<void> {
    const load = this.load();
    if (!load) return;

    const { ActionRequiredModalComponent } = await import(
      './action-required-modal/action-required-modal.component'
    );
    this.modalService.open(ActionRequiredModalComponent, {
      data: {
        load,
        mode,
        isEditingExisting,
        onCompleted: () => {
          this.dismissedResolvedBanner.set(false);
          this.loadUpdated.emit();
        },
      },
      title:
        mode === 'resolve'
          ? `Resolve Issue — Load #${load.externalId || load.id}`
          : isEditingExisting
            ? `Update Issue Note — Load #${load.externalId || load.id}`
            : `Flag Action Required — Load #${load.externalId || load.id}`,
      subtitle:
        mode === 'resolve'
          ? 'Share how this issue was resolved with your team'
          : 'Provide clear context so everyone knows what action is needed',
      icon: mode === 'resolve' ? 'CheckCircle2' : 'Flag',
      size: 'md',
    });
  }

  toggleActionRequired(): void {
    const load = this.load();
    if (!load) return;
    void this.openActionRequiredModal(load.actionRequiredFlag ? 'resolve' : 'flag');
  }

  formatAuditDate(ts: any): Date | null {
    if (!ts) return null;
    if (ts instanceof Date) return ts;
    if (typeof ts.toDate === 'function') return ts.toDate();
    if (typeof ts.seconds === 'number') return new Date(ts.seconds * 1000);
    return null;
  }

  private resolveDocUrl(raw: any): string | null {
    if (!raw) return null;
    if (typeof raw === 'string') return raw === 'N/A' ? null : raw.trim() || null;
    if (Array.isArray(raw)) {
      const latest = raw[raw.length - 1];
      return this.resolveDocUrl(latest);
    }
    if (typeof raw === 'object' && typeof raw.url === 'string') {
      return raw.url === 'N/A' ? null : raw.url.trim() || null;
    }
    return null;
  }

  private resolveDocType(url: string, defaultType: DocumentType = 'pdf'): DocumentType {
    try {
      const clean = decodeURIComponent(url.split('?')[0]).toLowerCase();
      if (clean.endsWith('.pdf') || clean.includes('.pdf')) return 'pdf';
      if (/\.(jpg|jpeg|png|webp|gif|bmp|heic|heif)$/i.test(clean)) return 'image';
    } catch {
      if (url.toLowerCase().includes('.pdf')) return 'pdf';
    }
    return defaultType;
  }

  // NEW: Handle document viewing based on context
  viewDocument(documentType: string, title: string): void {
    const load = this.load();
    if (!load) return;

    // Documents can live on parent (operational) or view (financial)
    const parentUrl = this.resolveDocUrl((load.documents as any)?.[documentType]);
    const viewUrl = this.resolveDocUrl((load as any)?.view?.documents?.[documentType]);
    const documentUrl = parentUrl ?? viewUrl;
    if (!documentUrl) return;

    const context = this.viewContext();
    const resolvedType = this.resolveDocType(documentUrl, 'pdf');

    if (context === 'details-page') {
      // On details page: Use the document viewer service (shows in right panel)
      this.documentViewer.setActiveDocument({
        id: `load-${load.id}-${documentType}`,
        url: documentUrl,
        type: resolvedType,
        title: `${title} - #${load.externalId || load.id}`,
        source: 'load',
        documentCategory: documentType,
      });
    } else {
      // On list page: Open in modal for quick viewing
      this.openDocumentModal(documentUrl, title, load.externalId || load.id, resolvedType);
    }
  }

  // Handle viewing documents when we already have the URL (e.g. from a stop card)
  viewDocumentUrl(url: string, title: string, documentCategory: string): void {
    const load = this.load();
    if (!load) return;

    const context = this.viewContext();
    const type: DocumentType = this.resolveDocType(
      url,
      documentCategory.includes('Image') ? 'image' : 'pdf'
    );

    if (context === 'details-page') {
      this.documentViewer.setActiveDocument({
        id: `load-${load.id}-${documentCategory}-${Date.now()}`,
        url,
        type,
        title: `${title} - #${load.externalId || load.id}`,
        source: 'load',
        documentCategory,
      });
    } else {
      this.openDocumentModal(url, title, load.externalId || load.id, type);
    }
  }

  viewFullDispatchSheet(): void {
    const url = this.fullDispatchSheetUrl();
    if (!url) return;
    this.viewDocumentUrl(url, 'Full Load Dispatch Sheet', 'dispatchSheet');
  }

  viewSegmentDispatchSheet(segment: LoadSegment): void {
    const url = this.getSegmentDispatchSheetUrl(segment);
    if (!url) return;
    const title = `Driver Sheet - ${segment.driverName || 'Segment ' + segment.segmentNumber} (Stops ${segment.fromStopNumber}-${segment.toStopNumber})`;
    this.viewDocumentUrl(url, title, 'dispatchSheet');
  }

  // NEW: Open document in modal
  private openDocumentModal(url: string, title: string, loadId: string, documentType: DocumentType): void {
    import(
      '../../../shared/ui/document-viewer-modal/document-viewer-modal.component'
    ).then(({ DocumentViewerModalComponent }) => {
      this.modalService.open(DocumentViewerModalComponent, {
        data: { url, documentType },
        title: `${title} - #${loadId}`,
        subtitle: 'View and download document',
        icon: 'FileText',
        size: 'xl',
      });
    });
  }
}
